import * as React from 'react'
import * as RadixDialog from '@radix-ui/react-dialog'
import { Icon } from '@blueprintjs/core'
import classNames from 'classnames'

/**
 * Drop-in replacement for Blueprint's `Dialog` from `@blueprintjs/core`.
 *
 * Goal: keep the Blueprint API surface so callsites can switch by changing the
 * import. Visuals are tuned to match Blueprint's `bp4-dialog`.
 *
 * Built on `@radix-ui/react-dialog` for focus-trap, scroll-lock, return-focus,
 * and ARIA out of the box — accessibility gaps that are painful to hand-roll.
 *
 * Audit-supported props (counts from the 2026-05-08 sweep audit, 25 files / 48
 * callsites):
 *   isOpen                 48 — required, controlled
 *   onClose                41 — fired for any close reason; mapped to onOpenChange(false)
 *   className              20 — utility classes (`p-3`, `p-0`, `bp4-card-rounded`, …)
 *   style                  18 — almost always {minWidth} / {width} overrides
 *   canOutsideClickClose    8 — boolean (default true)
 *   canEscapeKeyClose       5 — boolean (default true)
 *   usePortal               3 — always {false}; nested in tables that own scroll
 *   title                   3 — auto-rendered header path (with close button)
 *
 * Skipped (zero callsites): icon, transitionDuration, enforceFocus, lazy,
 * autoFocus, shouldReturnFocusOnClose, backdropClassName, hasBackdrop.
 *
 * Two layout patterns supported:
 *   1. Manual layout (22 of 25 files): callsite renders its own header inside
 *      `<div className={Classes.DIALOG_HEADER}>`, body inside DIALOG_BODY,
 *      footer inside DIALOG_FOOTER + DIALOG_FOOTER_ACTIONS. We re-export
 *      `Classes` with the same `bp4-dialog-*` string values so the existing
 *      Blueprint CSS continues to style them — no JSX rewrite required.
 *   2. Auto header (3 files): callsite passes `title="..."`. We render a
 *      Blueprint-style header with an X close button automatically.
 */

export interface DialogProps {
    isOpen: boolean
    onClose?: (event?: React.SyntheticEvent<HTMLElement>) => void
    title?: React.ReactNode
    /** Hides the auto-header close control for non-dismissible request states. */
    hideCloseButton?: boolean
    /** Optional screen-reader description for the dialog. */
    description?: React.ReactNode
    /** className applied to the dialog box (the inner panel, not the overlay). */
    className?: string
    /** Inline style on the dialog box — used for width / min-width overrides. */
    style?: React.CSSProperties
    canEscapeKeyClose?: boolean
    canOutsideClickClose?: boolean
    /** When false, render inline instead of portaling to document.body. */
    usePortal?: boolean
    /**
     * Override the stacking-wrapper z-index (default 20).
     * Use a higher value (e.g. 40) when the dialog must cover fixed elements
     * like a save bar or toolbar that sit at z-30.
     */
    zIndex?: number
    /**
     * Opt out of Radix's modal mode for this dialog.
     *
     * Radix's `modal={true}` (default) calls `disableOutsidePointerEvents`,
     * which sets `document.body.style.pointer-events: none` while the dialog
     * is open. The Content overrides it back to `auto` on its own subtree, so
     * the dialog body remains interactive — but anything else portaled to
     * `document.body` (Blueprint `Popover`, `MultiSelect`, `Suggest`, etc.)
     * inherits the `none` and becomes un-clickable.
     *
     * Set `nonModal` when the dialog contains a Blueprint portal-based
     * dropdown that the user needs to click into. The visual overlay we
     * render ourselves is unchanged — the only thing we give up is Radix's
     * focus trap and `aria-hidden` on siblings.
     */
    nonModal?: boolean
    children?: React.ReactNode
}

export const Dialog: React.FC<DialogProps> = ({
    isOpen,
    onClose,
    title,
    hideCloseButton = false,
    description,
    className,
    style,
    canEscapeKeyClose = true,
    canOutsideClickClose = true,
    usePortal = true,
    nonModal = false,
    zIndex,
    children,
}) => {
    if (!isOpen) {
        return null
    }

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            onClose?.()
        }
    }

    const handleEscapeKeyDown = (event: KeyboardEvent) => {
        if (!canEscapeKeyClose) {
            event.preventDefault()
        }
    }

    const handlePointerDownOutside = (event: Event) => {
        if (!canOutsideClickClose) {
            event.preventDefault()
        }
    }

    // Radix's onInteractOutside also fires for outside focus events. We mirror
    // Blueprint, where `canOutsideClickClose=false` blocks both pointer-down
    // and other outside interactions, by preventing both.
    const handleInteractOutside = (event: Event) => {
        if (!canOutsideClickClose) {
            event.preventDefault()
        }
    }

    // Layout:
    //   Stacking wrapper — position:fixed; inset:0; z-20  (own stacking context)
    //     Overlay        — absolute inset-0                (backdrop)
    //     Centering box  — absolute inset-0; flex          (centers Content)
    //       Content      — max-h-[calc(100vh-60px)]; overflow-y:auto (scroll here)
    //
    // Why the stacking wrapper: when two Dialogs are open simultaneously (e.g.
    // AddAsinsModal's confirm-on-submit pattern), each instance needs its
    // overlay+content to render entirely above any earlier dialog. With
    // hardcoded z-20/z-21 on overlay+content directly, the first dialog's
    // Content (z-21) ends up above the second dialog's Overlay (z-20) — same
    // z-index space, later DOM only wins ties. The fix: each Dialog's wrapper
    // sets z-20 (creating a stacking context via position:fixed + z-index),
    // and inside that context overlay/content stack relatively. Two wrappers
    // at the same z-index resolve by DOM order, but their *internal* z-indexes
    // are isolated — so the later wrapper's overlay covers the earlier
    // wrapper's content.
    //
    // The Content itself is the scroll container — NOT the Overlay. Radix's
    // `react-remove-scroll` (engaged by modal=true) intercepts wheel events
    // targeted at the Content tree and prevents them from propagating to
    // ancestors. So if the Overlay were the scroll container, wheel-on-dialog
    // would be trapped and nothing would scroll. Putting overflow-y:auto on
    // Content means the wheel target IS the scroll target — react-remove-scroll
    // happily allows scroll within an element that's itself scrollable.
    //
    // Trade-off vs Blueprint: Blueprint lets the dialog grow naturally past
    // viewport with the backdrop scrolling. Ours constrains dialog to viewport
    // and scrolls internally. UX-wise this is better (close button + footer
    // stay reachable when content is tall) and matches shadcn/Radix
    // conventions. Also avoids a real scroll-trap bug under modal mode.
    //
    // Pointer-events ladder:
    //   Overlay         pointer-events:auto  (clicks here → outside-click close)
    //   Centering box   pointer-events:none  (gutter clicks pass through to overlay)
    //   Dialog Content  pointer-events:auto  (clicks here stay)
    // In non-modal mode Radix's <Dialog.Overlay /> renders null (it's gated
    // on `modal === true`), so we'd lose the backdrop. Fall back to a plain
    // div with the same styling for the nonModal case — the visual modality
    // is independent of Radix's modal/focus behavior.
    const overlay = nonModal ? (
        <div className={classNames('absolute inset-0', 'bg-[rgba(17,20,24,0.7)]')} />
    ) : (
        <RadixDialog.Overlay
            className={classNames('absolute inset-0', 'bg-[rgba(17,20,24,0.7)]')}
        />
    )

    const content = (
        <div className="fixed inset-0" style={{ zIndex: zIndex ?? 20 }}>
            {overlay}
            <div
                className={classNames(
                    'absolute inset-0',
                    'flex items-center justify-center',
                    'pointer-events-none',
                )}
            >
                <RadixDialog.Content
                    // Match Blueprint's `bp4-dialog` styling. We don't include
                    // the `bp4-dialog` className — instead we reproduce its
                    // computed visuals here, to avoid picking up Blueprint
                    // compound rules we don't want.
                    className={classNames(
                        'flex flex-col bg-[#f6f7f9] rounded-[4px]',
                        'shadow-[0_0_0_1px_rgba(17,20,24,0.1),0_2px_4px_rgba(17,20,24,0.2),0_8px_24px_rgba(17,20,24,0.2)]',
                        'my-[30px] max-h-[calc(100vh-60px)] overflow-y-auto',
                        'pointer-events-auto select-text',
                        'w-[500px] focus:outline-none',
                        className,
                    )}
                    style={style}
                    {...(description === undefined ? { 'aria-describedby': undefined } : {})}
                    onEscapeKeyDown={handleEscapeKeyDown}
                    onPointerDownOutside={handlePointerDownOutside}
                    onInteractOutside={handleInteractOutside}
                >
                    {title !== undefined && title !== null ? (
                        <DialogAutoHeader
                            title={title}
                            onClose={onClose}
                            showCloseButton={!hideCloseButton}
                        />
                    ) : (
                        // Radix requires a Dialog.Title for screen readers.
                        // When the caller supplied one, DialogAutoHeader
                        // rendered it. For the manual-layout case, give a
                        // hidden one so the a11y tree stays valid (and Radix
                        // doesn't console.warn).
                        <RadixDialog.Title className="sr-only">Dialog</RadixDialog.Title>
                    )}
                    {description !== undefined && (
                        <RadixDialog.Description className="sr-only">
                            {description}
                        </RadixDialog.Description>
                    )}
                    {children}
                </RadixDialog.Content>
            </div>
        </div>
    )

    return (
        <RadixDialog.Root open={isOpen} onOpenChange={handleOpenChange} modal={!nonModal}>
            {usePortal ? <RadixDialog.Portal>{content}</RadixDialog.Portal> : content}
        </RadixDialog.Root>
    )
}

/**
 * Auto-rendered header for the `title` prop path. Mirrors Blueprint's
 * `.bp4-dialog-header` — white background, 40px min-height, hairline shadow,
 * Blueprint heading typography, and a close-button on the right.
 */
const DialogAutoHeader: React.FC<{
    title: React.ReactNode
    onClose?: (event?: React.SyntheticEvent<HTMLElement>) => void
    showCloseButton: boolean
}> = ({ title, onClose, showCloseButton }) => (
    <div
        className={classNames(
            'flex items-center bg-white rounded-t-[4px]',
            'shadow-[0_1px_0_rgba(17,20,24,0.15)]',
            'flex-none min-h-[40px] py-[5px] pr-[5px] pl-[15px]',
            // Sticky so the close button stays reachable when the dialog body
            // scrolls. `top: 0` is relative to the Content's scroll container.
            'sticky top-0 z-10',
        )}
    >
        <RadixDialog.Title asChild>
            <h4
                className={classNames(
                    'flex-1 m-0 overflow-hidden whitespace-nowrap',
                    'text-[16px] leading-[1.2] font-semibold text-[#1c2127]',
                )}
                style={{ textOverflow: 'ellipsis' }}
            >
                {title}
            </h4>
        </RadixDialog.Title>
        {showCloseButton && (
            <RadixDialog.Close asChild>
                <button
                    type="button"
                    aria-label="Close"
                    className={classNames(
                        'flex items-center justify-center',
                        'w-[30px] h-[30px] rounded-[2px] border-0 bg-transparent cursor-pointer',
                        'text-[#5f6b7c] hover:bg-[rgba(143,153,168,0.15)] hover:text-[#1c2127]',
                        'active:bg-[rgba(143,153,168,0.3)]',
                        'focus:outline-none',
                    )}
                    onClick={(e) => onClose?.(e)}
                >
                    <Icon icon="cross" />
                </button>
            </RadixDialog.Close>
        )}
    </div>
)

/**
 * Re-exported `Classes` constants. Same string values as
 * `@blueprintjs/core`'s `Classes`, so callsites that compose their own
 * header / body / footer with `<div className={Classes.DIALOG_BODY}>`
 * continue to look correct — Blueprint's CSS rules on `.bp4-dialog-*`
 * still apply.
 */
export const Classes = {
    DIALOG_HEADER: 'bp4-dialog-header',
    DIALOG_BODY: 'bp4-dialog-body',
    DIALOG_FOOTER: 'bp4-dialog-footer',
    DIALOG_FOOTER_ACTIONS: 'bp4-dialog-footer-actions',
    DIALOG_CLOSE_BUTTON: 'bp4-dialog-close-button',
} as const
